import { Input, Modal, Pagination, Spin, message } from "antd";
import type { CSSProperties } from "react";
import {
  NoteCard,
  STOCK_NOTE_CATEGORIES,
  STOCK_NOTES_CATEGORY_CONTENT_GAP,
  STOCK_NOTES_CATEGORY_TAG_ACTIVE_BORDER_COLOR,
  STOCK_NOTES_CATEGORY_TAG_BACKGROUND,
  STOCK_NOTES_CATEGORY_TAG_BORDER_RADIUS,
  STOCK_NOTES_CATEGORY_TAG_COLOR,
  STOCK_NOTES_CATEGORY_TAG_WIDTH,
  STOCK_NOTES_COMPOSE_BTN,
  STOCK_NOTES_CONTENT_AREA_BACKGROUND,
  STOCK_NOTES_CONTENT_AREA_BORDER_RADIUS,
  STOCK_NOTES_MODAL_BTN_BORDER_RADIUS,
  STOCK_NOTES_MODAL_HEIGHT,
  STOCK_NOTES_MODAL_SAVE_BTN_BACKGROUND,
  STOCK_NOTES_MODAL_SAVE_BTN_COLOR,
  STOCK_NOTES_TITLE_CATEGORY_GAP,
  type NoteValidityStatus,
  type StockNoteCategory,
  type StockNoteItem,
} from "fin-web-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStockLinkDetail } from "../../components/fin-web/useStockLinkDetail";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import { globalModel } from "../../global/GlobalModel";
import type { StockSearchBrief } from "../../global/GlobalType";
import { IS_STANDALONE } from "../../standalone/config";
import { UserStockNoteApi } from "../user_holdings/UserStockNoteApi";
import { MyNotesApi } from "./MyNotesApi";
import {
  MY_NOTES_COUNT_PADDING_LEFT,
  MY_NOTES_EMPTY_COMPOSE_COLOR,
  MY_NOTES_EMPTY_MARGIN_TOP,
  MY_NOTES_EMPTY_MOTTO,
  MY_NOTES_PAGE_SIZE,
  MY_NOTES_STATS_BTN_COLOR,
  MY_NOTES_STATS_BTN_COMPOSE_GAP,
  MY_NOTES_STATS_BTN_HOVER_COLOR,
  MY_NOTES_STATS_HERO_BACKGROUND,
  MY_NOTES_STATS_HERO_BODY_PADDING,
  MY_NOTES_STATS_HERO_BORDER_RADIUS,
  MY_NOTES_STATS_HERO_HEAD_PADDING,
  MY_NOTES_STATS_HERO_LABEL_FONT_SIZE,
  MY_NOTES_STATS_HERO_VALUE_FONT_SIZE,
  MY_NOTES_STATS_ITEM_BACKGROUND,
  MY_NOTES_STATS_ITEM_BORDER_RADIUS,
  MY_NOTES_STATS_ITEM_PADDING,
  MY_NOTES_STATS_LABEL_COLOR,
  MY_NOTES_STATS_LABEL_FONT_SIZE,
  MY_NOTES_STATS_MODAL_BACKGROUND,
  MY_NOTES_STATS_MODAL_BORDER_RADIUS,
  MY_NOTES_STATS_MODAL_PADDING,
  MY_NOTES_STATS_MODAL_WIDTH,
  MY_NOTES_STATS_RATE_VALUE_COLOR,
  MY_NOTES_STATS_TOTAL_FLEX,
  MY_NOTES_STATS_VALUE_COLOR,
  MY_NOTES_STATS_VALUE_FONT_SIZE,
  MY_NOTES_STOCK_FILTER_MARGIN_LEFT,
  MY_NOTES_STOCK_FILTER_WIDTH,
} from "./Config";
import type { MyNotesRow, MyNotesStatsResult, MyNotesValidityFilter } from "./MyNotesType";
import "./MyNotesPage.css";

export interface MyNotesPageProps {
  spaceId: string;
}

const VALIDITY_FILTERS: { key: MyNotesValidityFilter; label: string }[] = [
  { key: "all", label: "全部笔记" },
  { key: "valid", label: "有效" },
  { key: "invalid", label: "无效" },
  { key: "unmarked", label: "未标记有效性" },
];

const STATS_SECONDARY_ITEMS: {
  key: keyof MyNotesStatsResult;
  label: string;
  wide?: boolean;
}[] = [
  { key: "totalCount", label: "总笔记数", wide: true },
  { key: "markedCount", label: "已标记" },
  { key: "validCount", label: "有效" },
  { key: "invalidCount", label: "无效" },
];

const composeModalConfigStyle = {
  "--stock-notes-category-tag-bg": STOCK_NOTES_CATEGORY_TAG_BACKGROUND,
  "--stock-notes-category-tag-color": STOCK_NOTES_CATEGORY_TAG_COLOR,
  "--stock-notes-category-tag-active-border": STOCK_NOTES_CATEGORY_TAG_ACTIVE_BORDER_COLOR,
  "--stock-notes-category-tag-radius": `${STOCK_NOTES_CATEGORY_TAG_BORDER_RADIUS}px`,
  "--stock-notes-category-tag-width":
    STOCK_NOTES_CATEGORY_TAG_WIDTH > 0 ? `${STOCK_NOTES_CATEGORY_TAG_WIDTH}px` : "auto",
  "--stock-notes-category-tag-flex": "0 0 auto",
  "--stock-notes-title-category-gap": `${STOCK_NOTES_TITLE_CATEGORY_GAP}px`,
  "--stock-notes-category-content-gap": `${STOCK_NOTES_CATEGORY_CONTENT_GAP}px`,
  "--stock-notes-content-area-bg": STOCK_NOTES_CONTENT_AREA_BACKGROUND,
  "--stock-notes-content-area-radius": `${STOCK_NOTES_CONTENT_AREA_BORDER_RADIUS}px`,
  "--stock-notes-modal-btn-radius": `${STOCK_NOTES_MODAL_BTN_BORDER_RADIUS}px`,
  "--stock-notes-modal-save-bg": STOCK_NOTES_MODAL_SAVE_BTN_BACKGROUND,
  "--stock-notes-modal-save-color": STOCK_NOTES_MODAL_SAVE_BTN_COLOR,
  "--stock-notes-modal-height": `${STOCK_NOTES_MODAL_HEIGHT > 0 ? STOCK_NOTES_MODAL_HEIGHT : 400}px`,
} as CSSProperties;

const statsModalStyle = {
  "--my-notes-stats-modal-bg": MY_NOTES_STATS_MODAL_BACKGROUND,
  "--my-notes-stats-modal-radius": `${MY_NOTES_STATS_MODAL_BORDER_RADIUS}px`,
  "--my-notes-stats-hero-bg": MY_NOTES_STATS_HERO_BACKGROUND,
  "--my-notes-stats-hero-radius": `${MY_NOTES_STATS_HERO_BORDER_RADIUS}px`,
  "--my-notes-stats-hero-head-padding": MY_NOTES_STATS_HERO_HEAD_PADDING,
  "--my-notes-stats-hero-body-padding": MY_NOTES_STATS_HERO_BODY_PADDING,
  "--my-notes-stats-hero-label-size": `${MY_NOTES_STATS_HERO_LABEL_FONT_SIZE}px`,
  "--my-notes-stats-hero-value-size": `${MY_NOTES_STATS_HERO_VALUE_FONT_SIZE}px`,
  "--my-notes-stats-rate-color": MY_NOTES_STATS_RATE_VALUE_COLOR,
  "--my-notes-stats-item-bg": MY_NOTES_STATS_ITEM_BACKGROUND,
  "--my-notes-stats-item-radius": `${MY_NOTES_STATS_ITEM_BORDER_RADIUS}px`,
  "--my-notes-stats-item-padding": MY_NOTES_STATS_ITEM_PADDING,
  "--my-notes-stats-label-color": MY_NOTES_STATS_LABEL_COLOR,
  "--my-notes-stats-label-size": `${MY_NOTES_STATS_LABEL_FONT_SIZE}px`,
  "--my-notes-stats-value-color": MY_NOTES_STATS_VALUE_COLOR,
  "--my-notes-stats-value-size": `${MY_NOTES_STATS_VALUE_FONT_SIZE}px`,
  "--my-notes-stats-total-flex": String(MY_NOTES_STATS_TOTAL_FLEX),
} as CSSProperties;

function parseValidityStatus(raw: unknown): NoteValidityStatus | null {
  const s = String(raw ?? "").trim();
  if (s === "valid" || s === "invalid") {
    return s;
  }
  return null;
}

function toRow(dto: {
  id?: string;
  stockCode?: string;
  stockName?: string;
  eventTime?: string;
  content?: string;
  category?: string;
  validityStatus?: unknown;
}): MyNotesRow | null {
  const id = String(dto.id ?? "").trim();
  const stockCode = String(dto.stockCode ?? "").trim().toUpperCase();
  const content = String(dto.content ?? "").trim();
  if (!id || !stockCode || !content) {
    return null;
  }
  const categoryRaw = String(dto.category ?? "").trim() as StockNoteCategory;
  const category: StockNoteCategory =
    categoryRaw === "基本面" || categoryRaw === "消息面" || categoryRaw === "技术面"
      ? categoryRaw
      : "基本面";
  return {
    id,
    stockCode,
    stockName: String(dto.stockName ?? "").trim() || stockCode,
    eventTime: String(dto.eventTime ?? ""),
    content,
    category,
    validityStatus: parseValidityStatus(dto.validityStatus),
  };
}

function toNoteItem(row: MyNotesRow): StockNoteItem {
  return {
    id: row.id,
    eventTime: row.eventTime,
    content: row.content,
    category: row.category,
    validityStatus: row.validityStatus,
    stockCode: row.stockCode,
    stockName: row.stockName,
  };
}

function formatStatsValue(
  stats: MyNotesStatsResult,
  key: keyof MyNotesStatsResult,
  isRate?: boolean
): string {
  const value = stats[key];
  if (isRate) {
    if (value == null) {
      return "—";
    }
    return `${(Number(value) * 100).toFixed(1)}%`;
  }
  return String(value ?? 0);
}

export default observer(function MyNotesPage(props: MyNotesPageProps): JSX.Element {
  const { spaceId } = props;

  if (!globalModel.isLoggedIn) {
    if (IS_STANDALONE) {
      return <Navigate to="/tactik/intel" replace />;
    }
    return <Navigate to={`/space/${spaceId}/home`} replace />;
  }

  return <MyNotesPageContent {...props} />;
});

function MyNotesPageContent(props: MyNotesPageProps): JSX.Element {
  const { spaceId } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStock = searchParams.get("stock")?.trim().toUpperCase() || undefined;

  const [validityFilter, setValidityFilter] = useState<MyNotesValidityFilter>("all");
  const [filterStockCode, setFilterStockCode] = useState<string | undefined>(initialStock);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<MyNotesRow[]>([]);
  const [editing, setEditing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState<MyNotesStatsResult | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState("");
  const [draftCategory, setDraftCategory] = useState<StockNoteCategory>("基本面");
  const [composeStockCode, setComposeStockCode] = useState<string | undefined>(undefined);
  const [composeStockName, setComposeStockName] = useState("");
  const [composeContent, setComposeContent] = useState("");
  const [composeCategory, setComposeCategory] = useState<StockNoteCategory>("基本面");
  const [markingNoteId, setMarkingNoteId] = useState<string | null>(null);
  const [markingStatus, setMarkingStatus] = useState<NoteValidityStatus | null>(null);

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);

  const fetchList = useCallback(
    async (overridePageNum?: number) => {
      const targetPage = overridePageNum ?? pageNum;
      setLoading(true);
      try {
        const data = await MyNotesApi.listNotes({
          spaceId,
          stockCode: filterStockCode,
          validityFilter,
          pageNum: targetPage,
          pageSize: MY_NOTES_PAGE_SIZE,
        });
        const list = (data?.notes ?? []).map(toRow).filter((x): x is MyNotesRow => x != null);
        const nextTotal = data?.total ?? 0;
        if (nextTotal > 0 && list.length === 0 && targetPage > 1) {
          setPageNum(targetPage - 1);
          return;
        }
        setRows(list);
        setTotal(nextTotal);
        if (overridePageNum != null && overridePageNum !== pageNum) {
          setPageNum(overridePageNum);
        }
      } catch (e) {
        message.error(e instanceof Error ? e.message : "加载失败");
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [spaceId, filterStockCode, validityFilter, pageNum]
  );

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    void globalModel.loadOverseasStocksCatalog();
  }, []);

  useEffect(() => {
    const stock = filterStockCode?.trim();
    if (stock) {
      setSearchParams({ stock }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [filterStockCode, setSearchParams]);

  const openStats = useCallback(async () => {
    setStatsModalOpen(true);
    setStatsLoading(true);
    try {
      const data = await MyNotesApi.queryNoteStats({
        spaceId,
        stockCode: filterStockCode,
      });
      if (!data) {
        message.error("加载统计失败");
        setStats(null);
        return;
      }
      setStats(data);
    } catch (e) {
      message.error(e instanceof Error ? e.message : "加载统计失败");
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [spaceId, filterStockCode]);

  const closeStatsModal = useCallback(() => {
    setStatsModalOpen(false);
  }, []);

  const onSelectStock = useCallback((stock: StockSearchBrief) => {
    setFilterStockCode(stock.code.trim().toUpperCase());
    setPageNum(1);
  }, []);

  const onClearStock = useCallback(() => {
    setFilterStockCode(undefined);
    setPageNum(1);
  }, []);

  const onChangeValidityFilter = useCallback((filter: MyNotesValidityFilter) => {
    setValidityFilter(filter);
    setPageNum(1);
  }, []);

  const openCompose = useCallback(() => {
    setComposeStockCode(filterStockCode);
    setComposeStockName("");
    setComposeContent("");
    setComposeCategory("基本面");
    setComposeModalOpen(true);
  }, [filterStockCode]);

  const closeComposeModal = useCallback(() => {
    setComposeModalOpen(false);
  }, []);

  const onComposeStockSelect = useCallback((stock: StockSearchBrief) => {
    setComposeStockCode(stock.code.trim().toUpperCase());
    setComposeStockName(stock.name.trim());
  }, []);

  const onComposeStockCleared = useCallback(() => {
    setComposeStockCode(undefined);
    setComposeStockName("");
  }, []);

  const submitCompose = useCallback(async () => {
    const code = composeStockCode?.trim().toUpperCase();
    const text = composeContent.trim();
    if (!code || !text) {
      return;
    }
    setCreating(true);
    try {
      const created = await UserStockNoteApi.create(spaceId, {
        stockCode: code,
        stockName: composeStockName.trim() || code,
        content: text,
        category: composeCategory,
      });
      if (!created) {
        message.error("保存失败");
        return;
      }
      closeComposeModal();
      await fetchList(1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : "保存失败");
    } finally {
      setCreating(false);
    }
  }, [
    closeComposeModal,
    composeCategory,
    composeContent,
    composeStockCode,
    composeStockName,
    fetchList,
    spaceId,
  ]);

  const openEdit = useCallback((row: MyNotesRow) => {
    setEditingId(row.id);
    setDraftContent(row.content);
    setDraftCategory(row.category);
    setEditModalOpen(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingId(null);
  }, []);

  const submitEdit = useCallback(async () => {
    const text = draftContent.trim();
    if (!text || !editingId) {
      return;
    }
    setEditing(true);
    try {
      const updated = await UserStockNoteApi.update(spaceId, {
        noteId: editingId,
        content: text,
        category: draftCategory,
      });
      if (!updated) {
        message.error("保存失败");
        return;
      }
      const row = toRow(updated);
      if (row) {
        setRows((prev) => prev.map((n) => (n.id === row.id ? row : n)));
      }
      closeEditModal();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "保存失败");
    } finally {
      setEditing(false);
    }
  }, [closeEditModal, draftCategory, draftContent, editingId, spaceId]);

  const confirmDelete = useCallback(
    (noteId: string) => {
      Modal.confirm({
        title: "删除笔记",
        content: "确定删除这条笔记吗？",
        okText: "确认",
        cancelText: "取消",
        okButtonProps: { style: { minWidth: 120, borderRadius: 0 } },
        cancelButtonProps: { style: { minWidth: 120, borderRadius: 0 } },
        onOk: async () => {
          const ok = await UserStockNoteApi.delete(spaceId, noteId);
          if (!ok) {
            message.error("删除失败");
            return;
          }
          await fetchList();
        },
      });
    },
    [fetchList, spaceId]
  );

  const markValidity = useCallback(
    async (noteId: string, validityStatus: NoteValidityStatus) => {
      setMarkingNoteId(noteId);
      setMarkingStatus(validityStatus);
      try {
        const updated = await UserStockNoteApi.markValidity(spaceId, { noteId, validityStatus });
        if (!updated) {
          message.error("标记失败");
          return;
        }
        await fetchList();
      } catch (e) {
        message.error(e instanceof Error ? e.message : "标记失败");
      } finally {
        setMarkingNoteId(null);
        setMarkingStatus(null);
      }
    },
    [fetchList, spaceId]
  );

  const statsPanel = useMemo(() => {
    if (!stats) {
      return null;
    }
    return (
      <div className="my-notes-page__stats-layout">
        <div className="my-notes-page__stats-hero">
          <div className="my-notes-page__stats-hero-head">
            <div className="my-notes-page__stats-hero-label">笔记有效率</div>
          </div>
          <div className="my-notes-page__stats-hero-body">
            <div className="my-notes-page__stats-hero-value">
              {formatStatsValue(stats, "validRate", true)}
            </div>
          </div>
        </div>
        <div className="my-notes-page__stats-secondary">
          {STATS_SECONDARY_ITEMS.map((item) => (
            <div
              className={
                item.wide
                  ? "my-notes-page__stats-item my-notes-page__stats-item--wide"
                  : "my-notes-page__stats-item"
              }
              key={item.key}
            >
              <div className="my-notes-page__stats-label">{item.label}</div>
              <div className="my-notes-page__stats-value">
                {formatStatsValue(stats, item.key)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [stats]);

  return (
    <div className="my-notes-page">
      <div
        className="my-notes-page__filters"
        style={
          {
            "--my-notes-stats-btn-color": MY_NOTES_STATS_BTN_COLOR,
            "--my-notes-stats-btn-hover": MY_NOTES_STATS_BTN_HOVER_COLOR,
            "--my-notes-stats-btn-compose-gap": `${MY_NOTES_STATS_BTN_COMPOSE_GAP}px`,
          } as CSSProperties
        }
      >
        <div className="my-notes-page__validity-filters">
          {VALIDITY_FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={
                validityFilter === item.key
                  ? "my-notes-page__validity-btn my-notes-page__validity-btn--active"
                  : "my-notes-page__validity-btn"
              }
              onClick={() => onChangeValidityFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
          <div
            className="my-notes-page__stock-filter"
            style={{
              width: MY_NOTES_STOCK_FILTER_WIDTH,
              maxWidth: MY_NOTES_STOCK_FILTER_WIDTH,
              marginLeft: MY_NOTES_STOCK_FILTER_MARGIN_LEFT,
            }}
          >
            <SearchStockWithCatalog
              stockCode={filterStockCode}
              onSelect={onSelectStock}
              onCleared={onClearStock}
              placeholder="输入代码查看个股笔记"
              borderRadius={0}
              backgroundColor="#ffffff"
            />
          </div>
        </div>
        <div className="my-notes-page__actions">
          <button
            type="button"
            className="my-notes-page__stats-btn"
            onClick={() => void openStats()}
          >
            笔记统计
          </button>
          <button
            type="button"
            className="stock-notes-compose-btn my-notes-page__compose-btn"
            style={{
              border: STOCK_NOTES_COMPOSE_BTN.border,
              borderRadius: STOCK_NOTES_COMPOSE_BTN.borderRadius,
              background: STOCK_NOTES_COMPOSE_BTN.background,
              color: STOCK_NOTES_COMPOSE_BTN.color,
              fontSize: STOCK_NOTES_COMPOSE_BTN.fontSize,
            }}
            onClick={openCompose}
          >
            记笔记
          </button>
        </div>
      </div>

      <div
        className="my-notes-page__count"
        style={{ paddingLeft: MY_NOTES_COUNT_PADDING_LEFT }}
      >
        共{total}条
      </div>

      <Spin spinning={loading}>
        {rows.length === 0 && !loading ? (
          <p
            className="my-notes-page__empty"
            style={{ marginTop: MY_NOTES_EMPTY_MARGIN_TOP }}
          >
            <span>{MY_NOTES_EMPTY_MOTTO}</span>
            <button
              type="button"
              className="my-notes-page__empty-compose"
              style={{ color: MY_NOTES_EMPTY_COMPOSE_COLOR }}
              onClick={openCompose}
            >
              立即写笔记
            </button>
          </p>
        ) : (
          <>
            <ul className="my-notes-page__list">
              {rows.map((row) => {
                const code = row.stockCode;
                return (
                  <li className="my-notes-page__list-item" key={row.id}>
                    <NoteCard
                      note={toNoteItem(row)}
                      showStock
                      stockLinkProps={{
                        stockDetail: stockLinkDetail.code === code ? stockLinkDetail.data : null,
                        detailLoading: stockLinkDetail.code === code && stockLinkDetail.loading,
                        dayKlines: stockLinkDetail.code === code ? stockLinkDetail.dayKlines : null,
                        klineLoading: stockLinkDetail.code === code && stockLinkDetail.klineLoading,
                        onRequestDetail: requestStockDetailForLink,
                        stockNotesTab: buildStockNotesTabProps(code, row.stockName),
                        notesOnly:
                          stockLinkDetail.code === code ? stockLinkDetail.notesOnly : false,
                      }}
                      onEdit={() => openEdit(row)}
                      onDelete={() => confirmDelete(row.id)}
                      onMarkValid={() => markValidity(row.id, "valid")}
                      onMarkInvalid={() => markValidity(row.id, "invalid")}
                      markingValid={markingNoteId === row.id && markingStatus === "valid"}
                      markingInvalid={markingNoteId === row.id && markingStatus === "invalid"}
                    />
                  </li>
                );
              })}
            </ul>
            {total > MY_NOTES_PAGE_SIZE ? (
              <div className="my-notes-page__pagination">
                <Pagination
                  current={pageNum}
                  pageSize={MY_NOTES_PAGE_SIZE}
                  total={total}
                  showSizeChanger={false}
                  onChange={(p) => setPageNum(p)}
                />
              </div>
            ) : null}
          </>
        )}
      </Spin>

      <Modal
        title="笔记统计"
        open={statsModalOpen}
        onCancel={closeStatsModal}
        centered
        footer={null}
        width={MY_NOTES_STATS_MODAL_WIDTH}
        destroyOnClose
        className="my-notes-page__stats-modal"
        styles={{
          content: statsModalStyle,
          body: { padding: MY_NOTES_STATS_MODAL_PADDING },
        }}
      >
        <Spin spinning={statsLoading}>{statsPanel}</Spin>
      </Modal>

      <Modal
        title="记笔记"
        open={composeModalOpen}
        onCancel={closeComposeModal}
        centered
        okText="保存"
        cancelText="取消"
        okButtonProps={{
          className: "stock-notes-rect-btn stock-notes-save-btn",
          style: { minWidth: 120 },
          loading: creating,
          disabled: !composeStockCode?.trim() || !composeContent.trim(),
        }}
        cancelButtonProps={{
          className: "stock-notes-rect-btn stock-notes-cancel-btn",
          style: { minWidth: 120 },
        }}
        onOk={() => void submitCompose()}
        destroyOnClose
        className="stock-notes-compose-modal"
        styles={{ content: composeModalConfigStyle }}
      >
        <div className="stock-notes-compose">
          <div className="my-notes-page__compose-stock">
            <SearchStockWithCatalog
              stockCode={composeStockCode}
              onSelect={onComposeStockSelect}
              onCleared={onComposeStockCleared}
              enableHK
              enableUS
              placeholder="选择要记录的股票"
              borderRadius={3}
              backgroundColor="rgb(232, 232, 232)"
              focusBackgroundColor="rgb(242, 242, 242)"
            />
          </div>
          <div className="stock-notes-category-row">
            {STOCK_NOTE_CATEGORIES.map((item) => (
              <button
                key={item}
                type="button"
                className={`stock-notes-category-tag${
                  composeCategory === item ? " stock-notes-category-tag--active" : ""
                }`}
                onClick={() => setComposeCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="stock-notes-content-wrap">
            <Input.TextArea
              className="stock-notes-content-input"
              variant="borderless"
              placeholder="有什么想要记的，写在这里哦。"
              maxLength={500}
              value={composeContent}
              onChange={(e) => setComposeContent(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="编辑笔记"
        open={editModalOpen}
        onCancel={closeEditModal}
        centered
        okText="保存"
        cancelText="取消"
        okButtonProps={{
          style: { minWidth: 120, borderRadius: 0 },
          loading: editing,
          disabled: !draftContent.trim(),
        }}
        cancelButtonProps={{ style: { minWidth: 120, borderRadius: 0 } }}
        onOk={() => void submitEdit()}
        destroyOnClose
      >
        <div className="my-notes-page__modal">
          <div className="my-notes-page__modal-tags">
            {STOCK_NOTE_CATEGORIES.map((item) => (
              <button
                key={item}
                type="button"
                className={
                  draftCategory === item
                    ? "my-notes-page__modal-tag my-notes-page__modal-tag--active"
                    : "my-notes-page__modal-tag"
                }
                onClick={() => setDraftCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <Input.TextArea
            variant="borderless"
            className="my-notes-page__modal-input"
            placeholder="有什么想要记的，写在这里哦。"
            maxLength={500}
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 12 }}
          />
        </div>
      </Modal>
    </div>
  );
}
